import React, { useRef, useState, useCallback } from "react";
import { Camera, Upload, X, Image as ImageIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { useI18n } from "@/lib/i18n";

interface ImageCaptureInputProps {
    onImageSelected?: (base64: string, file: File) => void;
    onImagesSelected?: (base64s: string[], files: File[]) => void;
    onImages?: (base64s: string[]) => void; // Alias for backward compatibility
    onImage?: (base64: string) => void; // Alias for backward compatibility
    onProcessing?: (processing: boolean) => void;
    label?: string;
    className?: string;
    multiple?: boolean;
    mode?: "both" | "camera" | "gallery";
    compact?: boolean;
}

/**
 * Native Camera & Image Capture Input
 * - MOBILE: Opens camera directly (if supported/requested)
 * - DESKTOP: Opens file picker
 * - SCALING: Max 1500px width, JPEG 0.8 quality
 */
export function ImageCaptureInput({
    onImageSelected,
    onImagesSelected,
    onImages,
    onImage,
    onProcessing,
    label = "Camera",
    className,
    multiple = false,
    mode = "both",
    compact = false
}: ImageCaptureInputProps) {
    const { t } = useI18n();
    const [isInternalProcessing, setIsInternalProcessing] = useState(false);
    const isProcessing = isInternalProcessing;

    const setProcessingState = useCallback((state: boolean) => {
        setIsInternalProcessing(state);
        onProcessing?.(state);
    }, [onProcessing]);

    const processFiles = useCallback(async (files: FileList) => {
        setProcessingState(true);
        const fileArray = Array.from(files);
        const results: { base64: string; file: File }[] = [];

        try {
            for (const file of fileArray) {
                const result = await new Promise<{ base64: string; file: File }>((resolve) => {
                    const reader = new FileReader();
                    reader.onload = (e) => {
                        const img = new Image();
                        img.onload = () => {
                            try {
                                // Optimized MAX_WIDTH to 1080 to prevent vercel 4.5MB payload limit issues for 4 photos
                                const MAX_WIDTH = 1080;
                                let width = img.width;
                                let height = img.height;

                                if (width > MAX_WIDTH) {
                                    height = (img.height * MAX_WIDTH) / img.width;
                                    width = MAX_WIDTH;
                                }

                                const canvas = document.createElement('canvas');
                                canvas.width = width;
                                canvas.height = height;
                                const ctx = canvas.getContext('2d');

                                if (!ctx) {
                                    console.error("Could not get canvas context");
                                    resolve({ base64: "", file: file });
                                    return;
                                }

                                ctx.drawImage(img, 0, 0, width, height);

                                // Quality adjusted to 0.85 to prevent payload too large errors
                                const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
                                const base64 = dataUrl.split(',')[1];

                                // Create a scaled file using blob
                                canvas.toBlob((blob) => {
                                    try {
                                        // Fallback for older mobile browsers that might fail at 'new File()' from Blob
                                        let scaledFile: File;
                                        try {
                                            scaledFile = blob ? new File([blob], file.name.replace(/\.[^/.]+$/, ".jpg"), { type: 'image/jpeg' }) : file;
                                        } catch (fileErr) {
                                            console.warn("new File() failed, using blob/original", fileErr);
                                            scaledFile = file;
                                        }
                                        resolve({ base64, file: scaledFile });
                                    } catch (innerErr) {
                                        console.error("Error in toBlob callback:", innerErr);
                                        resolve({ base64, file: file });
                                    }
                                }, 'image/jpeg', 0.85);
                            } catch (e) {
                                console.error("Error processing image canvas:", e);
                                resolve({ base64: "", file: file });
                            }
                        };
                        img.onerror = (err) => {
                            console.error("Error loading image from data URL:", err);
                            // Fallback: try to just send the original file as base64 if it's not too big
                            if (file.size < 2 * 1024 * 1024) {
                                const base64Fallback = (e.target?.result as string).split(',')[1];
                                resolve({ base64: base64Fallback, file: file });
                            } else {
                                resolve({ base64: "", file: file });
                            }
                        };
                        img.src = e.target?.result as string;
                    };
                    reader.onerror = () => resolve({ base64: "", file: file });
                    reader.readAsDataURL(file);
                });
                results.push(result);
            }

            const validResults = results.filter(r => r.base64);

            if (multiple) {
                if (onImagesSelected) {
                    onImagesSelected(validResults.map(r => r.base64), validResults.map(r => r.file));
                }
                if (onImages) {
                    onImages(validResults.map(r => r.base64));
                }
            } else if (validResults[0]) {
                if (onImageSelected) {
                    onImageSelected(validResults[0].base64, validResults[0].file);
                }
                if (onImage) {
                    onImage(validResults[0].base64);
                }
            }
        } catch (error) {
            console.error("Major error processing files:", error);
        } finally {
            setProcessingState(false);
        }
    }, [multiple, onImageSelected, onImagesSelected, onImages, onImage, setProcessingState]);

    const showCamera = mode === "both" || mode === "camera";
    const showGallery = mode === "both" || mode === "gallery";

    return (
        <div className={cn(
            "flex justify-center items-center gap-2 w-full",
            compact ? "px-0" : "flex-col sm:flex-row gap-3 px-4",
            className
        )}>
            {showCamera && (
                <div className={cn(
                    "w-full sm:flex-1 md:hidden",
                    compact ? "min-w-0" : "min-w-[140px]"
                )}>
                    <label className={cn(
                        "relative flex items-center justify-center gap-2 bg-magenta text-white rounded-2xl font-bold transition-all active:scale-95 shadow-md cursor-pointer border-2 border-magenta w-full",
                        compact ? "h-8 px-2" : "h-12 px-3 py-3",
                        isProcessing ? "opacity-70 cursor-wait" : ""
                    )}>
                        <input
                            type="file"
                            accept="image/*"
                            capture="environment"
                            className="sr-only"
                            disabled={isProcessing}
                            onChange={(e) => {
                                if (e.target.files?.length) processFiles(e.target.files);
                                e.target.value = '';
                            }}
                        />
                        <Camera className={cn(compact ? "h-3 w-3" : "h-5 w-5", "flex-shrink-0")} />
                        <span className={cn("truncate", compact ? "text-[10px]" : "text-sm")}>{isProcessing ? t("common.processing") : t("common.takePhoto")}</span>
                    </label>
                </div>
            )}

            {showGallery && (
                <div className={cn(
                    "w-full sm:flex-1",
                    compact ? "min-w-0" : "min-w-[140px]"
                )}>
                    <label className={cn(
                        "relative flex items-center justify-center gap-2 bg-white text-magenta border-2 border-magenta rounded-2xl font-bold transition-all active:scale-95 shadow-sm cursor-pointer w-full",
                        compact ? "h-8 px-2" : "h-12 px-3 py-3",
                        isProcessing ? "opacity-70 cursor-wait" : ""
                    )}>
                        <input
                            type="file"
                            accept="image/*"
                            multiple={multiple}
                            className="sr-only"
                            disabled={isProcessing}
                            onChange={(e) => {
                                if (e.target.files?.length) processFiles(e.target.files);
                                e.target.value = '';
                            }}
                        />
                        <Upload className={cn(compact ? "h-3 w-3" : "h-5 w-5", "flex-shrink-0")} />
                        <span className={cn("truncate", compact ? "text-[10px]" : "text-sm")}>
                            {isProcessing ? t("common.processing") : (label || t("common.fromGallery"))}
                        </span>
                    </label>
                </div>
            )}
        </div>
    );
}

